/**
 * Where in `text` the pointer at (x, y) is, as a caret offset — or `null` when
 * the point is not over that text.
 *
 * Opening a cell for editing swaps the drawn text for a textarea, and a textarea
 * that mounts focused puts its caret at the start. The author pointed at a word;
 * this is the offset of that word, read from the drawn text BEFORE it is
 * swapped out, so the editor can open with the caret where they clicked.
 *
 * Only a text node that holds exactly `text` counts. The editor draws labels with
 * no variable values, so the drawn text IS the stored text; when the two differ
 * (a resolved token, the "Text label" placeholder on an empty element) an offset
 * into one is not an offset into the other, and no caret is better than a wrong
 * one.
 */
export function caretOffsetAtPoint(
  x: number,
  y: number,
  within: Element,
  text: string,
): number | null {
  let node: Node | null = null;
  let offset = 0;
  // `caretPositionFromPoint` is the standard (Firefox, Chrome 128+);
  // `caretRangeFromPoint` is what Safari still ships.
  if (typeof document.caretPositionFromPoint === 'function') {
    const pos = document.caretPositionFromPoint(x, y);
    node = pos?.offsetNode ?? null;
    offset = pos?.offset ?? 0;
  } else if (typeof document.caretRangeFromPoint === 'function') {
    const range = document.caretRangeFromPoint(x, y);
    node = range?.startContainer ?? null;
    offset = range?.startOffset ?? 0;
  }
  if (!node || node.nodeType !== Node.TEXT_NODE || !within.contains(node)) return null;
  if (node.textContent === text) return Math.min(offset, text.length);
  // Text with styles on part of it (DERP-307) is drawn as one span per run, so
  // no single text node holds it. The nearest element that holds exactly the
  // text does, and the offset is counted from its start.
  for (let el = node.parentElement; el && within.contains(el); el = el.parentElement) {
    if (el.textContent !== text) continue;
    const r = document.createRange();
    r.selectNodeContents(el);
    r.setEnd(node, offset);
    return Math.min(r.toString().length, text.length);
  }
  return null;
}
